import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  Eye,
  Trash2,
  TrendingUp,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Check,
  Database,
  Package,
  Settings,
  Zap,
  Star,
  Rocket,
  AlertTriangle,
  Box,
  Tag,
} from 'lucide-react'
import { useAppSelector, useAppDispatch } from '../../../../store/hooks'
import { selectSelectedModelId } from '../../../../store/slices/modelSlice'
import {
  useDeployModelMutation,
  useDeleteModelDeploymentMutation,
  useDeleteModelMutation,
  useStopTrainModelMutation,
  invalidateTags,
  modelsApi,
} from '../../../../services/modelsApi'
import { useGetModelQuery } from '../../../../services/modelsApi'
import { useAnnotationStats } from '../../../../store/hooks'
import { Model } from '../../../../types/models/model'
import ConfirmDialog from '../../../modals/ConfirmDialog'
import ErrorDialog from '../../../dialogs/ErrorDialog'
import {
  SidebarBackdrop,
  ModelDetailSidebar,
  ModelHeaderCard,
  ActionsSection,
  SidebarContent,
  DetailSection,
  MetricsGrid,
  MetricCard,
  DatasetCard,
  ClassDisplayCard,
  OverviewCard,
  ModelStatusBadge,
  DangerZone,
} from '../styles'
import { apiTags } from '../../../../services/apiTags'

// Interfaces
interface ProjectLite {
  id: string
  datasetid: string
  annotation_type: 'classification' | 'object_detection' | string
}

interface DatasetInfoLite {
  training: string[]
  validation: string[]
  imageSize: number | undefined
  epochs: number | undefined
  augmentation?: boolean
}

interface ModelDetailSidebarProps {
  model: Model
  project: ProjectLite
  onClose: () => void
  datasetInfo: DatasetInfoLite
  show?: boolean
  onDelete?: (deletedModelId: string) => void
}

// Main Component
const ModelDetailSidebarComponent = React.memo<ModelDetailSidebarProps>(
  ({ model, project, onClose, datasetInfo, show, onDelete }) => {
    const dispatch = useAppDispatch()
    const selectedModelId = useAppSelector(selectSelectedModelId)
    const isCurrentlySelected = selectedModelId === model.id

    // Add loading state
    const [isLoading, setIsLoading] = useState(true)

    // Deploy/Delete/Training functionality
    const [deployModel, { isLoading: isDeploying }] = useDeployModelMutation()
    const [deleteDeployment, { isLoading: isDeletingDeployment }] =
      useDeleteModelDeploymentMutation()
    const [deleteModel, { isLoading: isDeletingModel }] = useDeleteModelMutation()
    const [stopTrainModel, { isLoading: isStoppingTraining }] = useStopTrainModelMutation()
    const [showDeleteDeploymentConfirm, setShowDeleteDeploymentConfirm] = useState<string | null>(
      null
    )
    const [showDeleteModelConfirm, setShowDeleteModelConfirm] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    // Defer heavy data loading
    const [shouldLoadData, setShouldLoadData] = useState(false)

    // Local polling control for live model status while sidebar is visible and deployment is transitioning
    const isVisible = show ?? true

    const getDeploymentStatusForModel = useCallback((m: Model | undefined) => {
      if (!m || !m.deployment) return 'not_deployed'
      const status = m.deployment.status
      if (status === 'IN_SERVICE') return 'deployed'
      if (status === 'CREATING' || status === 'INITIALIZED') return 'deploying'
      if (status === 'ERR') return 'failed'
      return 'not_deployed'
    }, [])

    const [poll, setPoll] = useState<boolean>(() => {
      return getDeploymentStatusForModel(model) === 'deploying'
    })

    const { data: liveModel, error: modelError } = useGetModelQuery(model.id, {
      skip: !isVisible || !model?.id,
      pollingInterval: poll ? 3000 : 0,
      refetchOnMountOrArgChange: true,
    })

    // Skip polling if we get a 404 (model deleted)
    useEffect(() => {
      if (modelError && 'status' in modelError && modelError.status === 404) {
        setPoll(false)
      }
    }, [modelError])

    useEffect(() => {
      const status = getDeploymentStatusForModel(liveModel ?? model)
      setPoll(isVisible && status === 'deploying')
    }, [liveModel, model, isVisible, getDeploymentStatusForModel])

    // Get dataset stats for training and validation datasets (only when needed)
    const { data: trainStats } = useAnnotationStats(
      project?.id || '',
      project?.datasetid || '',
      datasetInfo.training,
      {
        skip:
          !shouldLoadData ||
          !project?.id ||
          !project?.datasetid ||
          datasetInfo.training.length === 0,
      }
    )

    const { data: validationStats } = useAnnotationStats(
      project?.id || '',
      project?.datasetid || '',
      datasetInfo.validation,
      {
        skip:
          !shouldLoadData ||
          !project?.id ||
          !project?.datasetid ||
          datasetInfo.validation.length === 0,
      }
    )

    // Load data after initial render
    useEffect(() => {
      const timer = setTimeout(() => {
        setShouldLoadData(true)
        setIsLoading(false)
      }, 50)

      return () => clearTimeout(timer)
    }, [])

    const effectiveModel: Model = useMemo(() => liveModel ?? model, [liveModel, model])

    const navigate = useNavigate()

    const handleSelectModel = useCallback(() => {
      // If model is initialized, navigate to training view to continue training
      if (effectiveModel.state === 'INITIALIZED') {
        navigate(`/training/${effectiveModel.projectid}?modelId=${effectiveModel.id}`)
        onClose() // Close the sidebar
        return
      }

      // For other states, no action needed (model selection is not used anymore)
    }, [effectiveModel.state, effectiveModel.projectid, effectiveModel.id, navigate, onClose])

    const handleDeploy = useCallback(async () => {
      try {
        await deployModel({ id: effectiveModel.id }).unwrap()
        setTimeout(() => {
          dispatch(
            modelsApi.util.invalidateTags([
              { type: apiTags.Model, id: effectiveModel.id },
              { type: apiTags.Model, id: 'LIST' },
            ])
          )
        }, 1500)
      } catch (error: unknown) {
        const message = (error as { data?: { message?: string } })?.data?.message
        setError(message || 'Failed to deploy model')
      }
    }, [deployModel, effectiveModel?.id, dispatch])

    const handleDeleteDeployment = useCallback(async () => {
      try {
        await deleteDeployment({ id: effectiveModel.id }).unwrap()
        setShowDeleteDeploymentConfirm(null)
        dispatch(
          modelsApi.util.invalidateTags([
            { type: apiTags.Model, id: effectiveModel.id },
            { type: apiTags.Model, id: 'LIST' },
          ])
        )
      } catch (error: unknown) {
        const message = (error as { data?: { message?: string } })?.data?.message
        setError(message || 'Failed to remove deployment')
      }
    }, [deleteDeployment, effectiveModel?.id, dispatch])

    const handleDeleteModel = useCallback(async () => {
      try {
        await deleteModel({ id: effectiveModel.id }).unwrap()
        setShowDeleteModelConfirm(null)
        onDelete?.(effectiveModel.id)
        onClose()
        dispatch(
          modelsApi.util.invalidateTags([
            { type: apiTags.Model, id: effectiveModel.id },
            { type: apiTags.Model, id: 'LIST' },
          ])
        )
      } catch (error: unknown) {
        const message = (error as { data?: { message?: string } })?.data?.message
        setError(message || 'Failed to delete model')
      }
    }, [deleteModel, effectiveModel?.id, onDelete, onClose, dispatch])

    const deploymentStatus = useMemo(
      () => getDeploymentStatusForModel(effectiveModel),
      [effectiveModel, getDeploymentStatusForModel]
    )

    const handleStopTraining = useCallback(async () => {
      try {
        await stopTrainModel({ id: effectiveModel.id }).unwrap()
        dispatch(
          modelsApi.util.invalidateTags([
            { type: apiTags.Model, id: effectiveModel.id },
            { type: apiTags.Model, id: 'LIST' },
          ])
        )
      } catch (error: unknown) {
        const message = (error as { data?: { message?: string } })?.data?.message
        setError(message || 'Failed to stop training')
      }
    }, [stopTrainModel, effectiveModel?.id, dispatch])

    const getModelStatus = useCallback((m: Model) => {
      if (m.state === 'TRAINING') return 'training'
      if (m.state === 'TRAINED') return 'trained'
      if (m.state === 'ERR') return 'error'
      return 'unknown'
    }, [])

    const performanceMetrics = useMemo(() => {
      const isClassification = project?.annotation_type === 'classification'
      const metrics = effectiveModel?.metrics || {}
      if (isClassification) {
        return [
          {
            label: 'Train Accuracy',
            value: (metrics?.['train:accuracy'] as number)
              ? `${(((metrics['train:accuracy'] as number) || 0) * 100).toFixed(1)}%`
              : 'N/A',
            icon: TrendingUp,
            description: 'Final training accuracy achieved',
          },
          {
            label: 'Validation Accuracy',
            value: (metrics?.['validation:accuracy'] as number)
              ? `${(((metrics['validation:accuracy'] as number) || 0) * 100).toFixed(1)}%`
              : 'N/A',
            icon: BarChart3,
            description: 'Final validation accuracy achieved',
          },
        ]
      }
      return [
        {
          label: 'mAP@50',
          value: (metrics?.['validation:mAP50'] as number)
            ? `${(((metrics['validation:mAP50'] as number) || 0) * 100).toFixed(1)}%`
            : 'N/A',
          icon: TrendingUp,
          description: 'Mean Average Precision at IoU threshold 0.5',
        },
        {
          label: 'mAP@50-95',
          value: (metrics?.['validation:mAP50-95'] as number)
            ? `${(((metrics['validation:mAP50-95'] as number) || 0) * 100).toFixed(1)}%`
            : 'N/A',
          icon: BarChart3,
          description: 'Mean Average Precision averaged across IoU thresholds 0.5-0.95',
        },
        {
          label: 'Precision',
          value: (metrics?.['validation:precision'] as number)
            ? `${(((metrics['validation:precision'] as number) || 0) * 100).toFixed(1)}%`
            : 'N/A',
          icon: ArrowUp,
          description: 'Ratio of true positive predictions to total positive predictions',
        },
        {
          label: 'Recall',
          value: (metrics?.['validation:recall'] as number)
            ? `${(((metrics['validation:recall'] as number) || 0) * 100).toFixed(1)}%`
            : 'N/A',
          icon: ArrowDown,
          description: 'Ratio of true positive predictions to total actual positives',
        },
      ]
    }, [project?.annotation_type, effectiveModel])

    // Get class names from annotation stats
    const classInfo = useMemo(() => {
      if (isLoading || !shouldLoadData) {
        return { classNames: [] as string[], totalClasses: 0 }
      }

      const toNames = (perClass?: Record<string, { name?: string | null }>): string[] =>
        perClass
          ? (Object.values(perClass) as Array<{ name?: string | null }>)
              .map((cls) => cls.name)
              .filter((name): name is string => !!name && name !== 'Unknown' && name !== 'null')
          : []

      const trainClassNames = toNames(
        trainStats?.statistics?.annotations_per_class as Record<string, { name?: string | null }>
      )
      const validationClassNames = toNames(
        validationStats?.statistics?.annotations_per_class as Record<
          string,
          { name?: string | null }
        >
      )

      const allClassNames = [...new Set([...trainClassNames, ...validationClassNames])]

      return {
        classNames: allClassNames,
        totalClasses: allClassNames.length,
      }
    }, [isLoading, shouldLoadData, trainStats, validationStats])

    const fullDatasetInfo = useMemo(() => {
      if (isLoading || !shouldLoadData) {
        return {
          ...datasetInfo,
          trainImageCount: 0,
          validationImageCount: 0,
          trainAnnotations: 0,
          validationAnnotations: 0,
        }
      }

      const trainImageCount =
        (trainStats?.statistics?.total_annotated_images || 0) +
        (trainStats?.statistics?.total_unannotated_images || 0)
      const validationImageCount =
        (validationStats?.statistics?.total_annotated_images || 0) +
        (validationStats?.statistics?.total_unannotated_images || 0)

      return {
        ...datasetInfo,
        trainImageCount,
        validationImageCount,
        trainAnnotations: trainStats?.statistics?.total_annotations || 0,
        validationAnnotations: validationStats?.statistics?.total_annotations || 0,
      }
    }, [isLoading, shouldLoadData, datasetInfo, trainStats, validationStats])

    return (
      <>
        <SidebarBackdrop $show={isVisible} onClick={onClose} />
        <ModelDetailSidebar $show={isVisible}>
          <ModelHeaderCard>
            <div className="header-left">
              <div className="model-name">{effectiveModel.name}</div>
              <div
                className={`model-type ${
                  project?.annotation_type === 'classification'
                    ? 'classification'
                    : 'object-detection'
                }`}
              >
                {project?.annotation_type === 'classification' ? (
                  <>
                    <Tag size={12} />
                    Classification
                  </>
                ) : (
                  <>
                    <Box size={12} />
                    Object Detection
                  </>
                )}
              </div>
            </div>
            <div className="header-right">
              <div className="model-status">
                <ModelStatusBadge $status={getModelStatus(effectiveModel)}>
                  {getModelStatus(effectiveModel)}
                </ModelStatusBadge>
              </div>
              <button className="close-button" onClick={onClose} aria-label="Close sidebar">
                <X size={16} />
              </button>
            </div>
          </ModelHeaderCard>

          <ActionsSection>
            <div className="left-actions">
              <button
                className={`select-button ${isCurrentlySelected ? 'selected' : ''}`}
                onClick={handleSelectModel}
                disabled={isCurrentlySelected}
                aria-label={
                  isCurrentlySelected
                    ? 'Currently selected model'
                    : effectiveModel.state === 'INITIALIZED'
                    ? 'Continue training this model'
                    : 'Select this model'
                }
              >
                {isCurrentlySelected ? (
                  <>
                    <Star size={16} />
                    Currently Selected
                  </>
                ) : effectiveModel.state === 'INITIALIZED' ? (
                  <>
                    <Rocket size={16} />
                    Continue Training
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Select Model
                  </>
                )}
              </button>
            </div>
            <div className="right-actions">
              {effectiveModel.state.toLowerCase() === 'trained' && (
                <button
                  className={`deploy-button ${
                    deploymentStatus === 'deployed'
                      ? 'deployed'
                      : deploymentStatus === 'deploying' || isDeploying || isDeletingDeployment
                      ? 'deploying'
                      : ''
                  }`}
                  onClick={
                    deploymentStatus === 'deployed'
                      ? () => setShowDeleteDeploymentConfirm(effectiveModel.id)
                      : handleDeploy
                  }
                  disabled={deploymentStatus === 'deploying' || isDeploying || isDeletingDeployment}
                  aria-label={
                    deploymentStatus === 'deployed'
                      ? isDeletingDeployment
                        ? 'Removing deployment'
                        : 'Undeploy model'
                      : isDeploying
                      ? 'Deploying model'
                      : 'Deploy model'
                  }
                >
                  <Rocket size={16} />
                  {deploymentStatus === 'deployed'
                    ? isDeletingDeployment
                      ? 'Removing...'
                      : 'Undeploy'
                    : deploymentStatus === 'deploying' || isDeploying
                    ? 'Deploying...'
                    : 'Deploy'}
                </button>
              )}
            </div>
          </ActionsSection>

          <SidebarContent>
            <div className="content-inner">
              {isLoading ? (
                <div
                  className="d-flex align-items-center justify-content-center text-secondary fs-6"
                  style={{ height: 200 }}
                >
                  Loading model details...
                </div>
              ) : (
                <>
                  {classInfo.classNames.length > 0 && (
                    <DetailSection>
                      <div className="section-title">
                        <Eye size={18} />
                        What This Model Detects
                      </div>
                      <div className="section-content">
                        <ClassDisplayCard>
                          <div className="classes-header">
                            <div className="classes-title">
                              <Package size={16} />
                              Detection Classes
                            </div>
                            <div className="classes-count">{classInfo.totalClasses}</div>
                          </div>
                          <div className="classes-grid">
                            {classInfo.classNames.map((className: string, index: number) => (
                              <div key={index} className="class-tag">
                                {className}
                              </div>
                            ))}
                          </div>
                        </ClassDisplayCard>
                      </div>
                    </DetailSection>
                  )}

                  <DetailSection>
                    <div className="section-title">
                      <Database size={18} />
                      Training Data
                    </div>
                    <div className="section-content">
                      <DatasetCard $type="training">
                        <div className="dataset-header">
                          <div className="dataset-indicator" />
                          <div className="dataset-title">Training Dataset</div>
                        </div>
                        <div className="fs-6 text-secondary mb-3 lh-sm">
                          Sources:{' '}
                          {fullDatasetInfo.training.length > 0
                            ? fullDatasetInfo.training.join(', ')
                            : 'N/A'}
                        </div>
                        <div className="dataset-stats">
                          <div className="stat-item">
                            <div className="stat-value">
                              {fullDatasetInfo.trainImageCount.toLocaleString()}
                            </div>
                            <div className="stat-label">Images</div>
                          </div>
                          <div className="stat-item">
                            <div className="stat-value">
                              {fullDatasetInfo.trainAnnotations.toLocaleString()}
                            </div>
                            <div className="stat-label">Annotations</div>
                          </div>
                        </div>
                      </DatasetCard>

                      {fullDatasetInfo.validation.length > 0 && (
                        <DatasetCard $type="validation">
                          <div className="dataset-header">
                            <div className="dataset-indicator" />
                            <div className="dataset-title">Validation Dataset</div>
                          </div>
                          <div className="fs-6 text-secondary mb-3 lh-sm">
                            Sources: {fullDatasetInfo.validation.join(', ')}
                          </div>
                          <div className="dataset-stats">
                            <div className="stat-item">
                              <div className="stat-value">
                                {fullDatasetInfo.validationImageCount.toLocaleString()}
                              </div>
                              <div className="stat-label">Images</div>
                            </div>
                            <div className="stat-item">
                              <div className="stat-value">
                                {fullDatasetInfo.validationAnnotations.toLocaleString()}
                              </div>
                              <div className="stat-label">Annotations</div>
                            </div>
                          </div>
                        </DatasetCard>
                      )}
                    </div>
                  </DetailSection>

                  {(effectiveModel.state.toLowerCase() === 'training' ||
                    effectiveModel.state.toLowerCase() === 'stopping') && (
                    <DetailSection>
                      <div className="section-title">
                        <Zap size={18} />
                        Quick Actions
                      </div>
                      <div className="section-content">
                        <div className="d-grid">
                          <button
                            className="btn btn-warning fw-semibold"
                            onClick={handleStopTraining}
                            disabled={
                              isStoppingTraining ||
                              effectiveModel.state.toLowerCase() === 'stopping'
                            }
                            aria-label={isStoppingTraining ? 'Stopping training' : 'Stop training'}
                          >
                            {isStoppingTraining
                              ? 'Stopping...'
                              : effectiveModel.state.toLowerCase() === 'stopping'
                              ? 'Stopping...'
                              : 'Stop Training'}
                          </button>
                        </div>
                      </div>
                    </DetailSection>
                  )}

                  {(effectiveModel.state.toLowerCase() === 'initialized' ||
                    effectiveModel.state.toLowerCase() === 'initialised') && (
                    <DetailSection>
                      <div className="section-title">
                        <Zap size={18} />
                        Quick Actions
                      </div>
                      <div className="section-content">
                        <div className="d-grid gap-2">
                          <button
                            className="btn btn-primary fw-semibold"
                            onClick={() => {
                              navigate(
                                `/training/${effectiveModel.projectid}?modelId=${effectiveModel.id}`
                              )
                              onClose()
                            }}
                            aria-label="Continue setting up training"
                          >
                            <Settings size={16} style={{ marginRight: '0.5rem' }} />
                            Continue Training Setup
                          </button>
                          <button
                            className="btn btn-danger fw-semibold"
                            onClick={() => setShowDeleteModelConfirm(effectiveModel.id)}
                            disabled={isDeletingModel}
                            aria-label={isDeletingModel ? 'Deleting model' : 'Delete model'}
                          >
                            <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                            {isDeletingModel ? 'Deleting...' : 'Delete Model'}
                          </button>
                        </div>
                      </div>
                    </DetailSection>
                  )}

                  {effectiveModel.state.toLowerCase() === 'trained' &&
                    performanceMetrics.length > 0 && (
                      <DetailSection>
                        <div className="section-title">
                          <TrendingUp size={18} />
                          Performance Metrics
                        </div>
                        <div className="section-content">
                          <MetricsGrid>
                            {performanceMetrics.map((metric, index) => (
                              <MetricCard key={index} title={metric.description}>
                                <div className="metric-icon">
                                  <metric.icon size={16} />
                                </div>
                                <div className="metric-value">{metric.value}</div>
                                <div className="metric-label">{metric.label}</div>
                              </MetricCard>
                            ))}
                          </MetricsGrid>
                        </div>
                      </DetailSection>
                    )}

                  <DetailSection>
                    <div className="section-title">
                      <Settings size={18} />
                      Model Configuration
                    </div>
                    <div className="section-content">
                      <OverviewCard>
                        <div className="overview-item">
                          <div className="overview-label">Input Size</div>
                          <div className="overview-value">{fullDatasetInfo.imageSize}px</div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-label">Epochs Trained</div>
                          <div className="overview-value">{fullDatasetInfo.epochs}</div>
                        </div>
                      </OverviewCard>

                      <div className="d-flex align-items-center justify-content-center gap-3 p-3 border rounded mt-3">
                        <div className="d-flex align-items-center gap-2">
                          <Zap
                            size={18}
                            className={fullDatasetInfo.augmentation ? 'text-success' : 'text-muted'}
                          />
                          <span className="text-secondary fw-semibold">Data Augmentation:</span>
                          <span
                            className={
                              fullDatasetInfo.augmentation
                                ? 'text-success fw-semibold'
                                : 'text-muted fw-semibold'
                            }
                          >
                            {fullDatasetInfo.augmentation ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </DetailSection>
                </>
              )}
            </div>
          </SidebarContent>

          <DangerZone>
            <div className="danger-header">
              <AlertTriangle size={16} />
              Danger Zone
            </div>
            <div className="danger-description">
              Permanently delete this model. This action cannot be undone and will remove all model
              data.
            </div>
            <button
              className="delete-button"
              onClick={() => setShowDeleteModelConfirm(model.id)}
              disabled={isDeletingModel}
              aria-label={isDeletingModel ? 'Deleting model' : 'Delete model permanently'}
            >
              <Trash2 size={16} />
              {isDeletingModel ? 'Deleting...' : 'Delete Model Permanently'}
            </button>
          </DangerZone>

          {/* Confirmation Dialogs */}
          <ConfirmDialog
            show={!!showDeleteDeploymentConfirm}
            onHide={() => setShowDeleteDeploymentConfirm(null)}
            title="Remove Deployment"
            message="Are you sure you want to remove this model's deployment? This will stop the model from serving predictions."
            confirmRightText="Remove Deployment"
            cancelText="Cancel"
            onConfirmRight={() => handleDeleteDeployment()}
            variant="danger"
          />

          <ConfirmDialog
            show={!!showDeleteModelConfirm}
            onHide={() => setShowDeleteModelConfirm(null)}
            title="Delete Model"
            message="Are you sure you want to delete this model? This action cannot be undone and will permanently remove the model and all its data."
            confirmRightText="Delete Model"
            cancelText="Cancel"
            onConfirmRight={() => handleDeleteModel()}
            variant="danger"
          />

          <ErrorDialog show={!!error} onClose={() => setError(null)} message={error || ''} />
        </ModelDetailSidebar>
      </>
    )
  },
  (prev, next) => {
    if (prev.show !== next.show) return false
    if (prev.model.id !== next.model.id) return false
    if (prev.project.id !== next.project.id) return false

    const arraysEqual = (a?: string[], b?: string[]) => {
      if (a === b) return true
      if (!a || !b) return false
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false
      }
      return true
    }

    if (prev.datasetInfo.imageSize !== next.datasetInfo.imageSize) return false
    if (prev.datasetInfo.epochs !== next.datasetInfo.epochs) return false
    if ((prev.datasetInfo.augmentation || false) !== (next.datasetInfo.augmentation || false)) {
      return false
    }
    if (!arraysEqual(prev.datasetInfo.training, next.datasetInfo.training)) return false
    if (!arraysEqual(prev.datasetInfo.validation, next.datasetInfo.validation)) return false

    return true
  }
)

export default ModelDetailSidebarComponent
